/**
 * Colony panel — expected happiness, arrival, and the state of the switch.
 *
 * The projection is the point of this panel. Before anyone arrives it answers
 * "if I invite colonists now, will they be content?", broken down by need so the
 * player can see WHICH need is short and go build for it. After arrival it shows
 * the same four numbers for the people actually there — same calculation, so the
 * promise made before committing is the one being kept.
 */

import {
  colonyCapacity,
  foodDemand,
  projectedHappiness,
  scoreHappiness,
  totalFood,
} from '../../sim/colony';
import { BUILDINGS, BUILDING_IDS } from '../../sim/data';
import type { GameState } from '../../sim/types';
import { compact, el, section, setText, type Issue, type Panel } from './panel';

function percent(bp: number): string {
  return `${Math.round(bp / 100)}%`;
}

/** Colour a satisfaction figure by how bad it is. */
function needClass(bp: number): string {
  if (bp >= 8000) return 'row-value';
  if (bp >= 5000) return 'row-value warn';
  return 'row-value danger';
}

export function createColonyPanel(issue: Issue): Panel {
  const { root, body } = section('Colony');

  const headline = el('div', 'power-headline');
  const modeHint = el('div', 'hint');

  const barTrack = el('div', 'bar-track');
  const barFill = el('div', 'bar-fill');
  barTrack.append(barFill);

  function needRow(label: string): { root: HTMLDivElement; value: HTMLSpanElement } {
    const row = el('div', 'row');
    row.append(el('span', 'row-label', label));
    const value = el('span', 'row-value');
    row.append(value);
    return { root: row, value };
  }

  const food = needRow('Food');
  const water = needRow('Water');
  const housing = needRow('Housing');
  const amenity = needRow('Amenity');

  const stats = el('div', 'hint');

  const controls = el('div', 'button-row');
  const arrive = el('button', 'action', 'Request colonists');
  arrive.addEventListener('click', () => {
    issue({ type: 'requestColonists' });
  });
  controls.append(arrive);

  body.append(
    headline,
    modeHint,
    barTrack,
    food.root,
    water.root,
    housing.root,
    amenity.root,
    stats,
    controls,
  );

  return {
    root,
    update(state: GameState): void {
      const colony = state.colony;
      const capacity = colonyCapacity(state);
      const arrived = colony.population > 0;

      // Before anyone lands, show the projection. After, show reality. Both come
      // from scoreHappiness, so the figure never changes meaning underneath the
      // player.
      const happiness = arrived
        ? scoreHappiness(
            capacity,
            colony.population,
            Math.min(totalFood(colony.food), foodDemand(colony.population)),
          )
        : projectedHappiness(state);

      // With no housing switched on the projection is for a population of zero,
      // and scoring zero people as fully content is true but useless — it reads
      // as "100% happy" beside an empty grid. Say there is nothing to project.
      const projectable = arrived || capacity.livingSpace > 0;

      setText(
        headline,
        arrived
          ? `${compact(colony.population)} colonists · ${percent(happiness.overallBp)} happy`
          : projectable
            ? `Expected happiness ${percent(happiness.overallBp)}`
            : 'No colony yet',
      );
      headline.className =
        !projectable
          ? 'power-headline'
          : happiness.overallBp >= 6500
          ? 'power-headline'
          : happiness.overallBp >= 3500
            ? 'power-headline warn'
            : 'power-headline danger';

      setText(
        modeHint,
        arrived
          ? `Housing for ${compact(capacity.livingSpace)} · ${compact(totalFood(colony.food))} food stored`
          : capacity.livingSpace === 0
            ? 'Build and switch on habitation before inviting anyone.'
            : `If ${compact(capacity.livingSpace)} colonists arrived now.`,
      );

      barFill.style.width = projectable
        ? `${Math.min(100, happiness.overallBp / 100).toFixed(1)}%`
        : '0%';
      barFill.className =
        happiness.overallBp >= 6500
          ? 'bar-fill'
          : happiness.overallBp >= 3500
            ? 'bar-fill warn'
            : 'bar-fill danger';

      const show = (bp: number): string => (projectable ? percent(bp) : '—');
      setText(food.value, show(happiness.foodBp));
      food.value.className = needClass(happiness.foodBp);
      setText(water.value, show(happiness.waterBp));
      water.value.className = needClass(happiness.waterBp);
      setText(housing.value, show(happiness.housingBp));
      housing.value.className = needClass(happiness.housingBp);
      setText(amenity.value, show(happiness.amenityBp));
      amenity.value.className = needClass(happiness.amenityBp);

      setText(
        stats,
        `${compact(capacity.foodPerTick)} food/tick · arrived ${compact(colony.totalArrived)}` +
          (colony.totalDeparted > 0 ? ` · left ${compact(colony.totalDeparted)}` : ''),
      );

      arrive.disabled = capacity.livingSpace <= colony.population;
    },
  };
}

/**
 * Building switchboard — how many of each type are built, and how many running.
 *
 * Buildings complete switched off, so without this the player has no way to see
 * that a city of thirty power plants is producing nothing. The per-type toggles
 * are what makes "bring the colony online" a single action rather than 616
 * right-clicks.
 */
export function createSwitchboardPanel(issue: Issue): Panel {
  const { root, body } = section('Buildings');

  const hint = el('div', 'hint', 'Buildings finish switched off.');
  body.append(hint);

  const rows = BUILDING_IDS.map((id) => {
    const row = el('div', 'row');
    const label = el('span', 'row-label', BUILDINGS[id].label);
    const count = el('span', 'row-value');

    const on = el('button', 'prio switch-on', 'on');
    on.title = `Switch on every completed ${BUILDINGS[id].label}`;
    const off = el('button', 'prio switch-off', 'off');
    off.title = `Switch off every completed ${BUILDINGS[id].label}`;

    // Held so the click handlers can emit one command per plot without reading
    // state directly — the panel never touches the simulation.
    let plots: Array<{ x: number; y: number }> = [];

    on.addEventListener('click', () => {
      for (const plot of plots) issue({ type: 'setBuildingEnabled', plot, enabled: true });
    });
    off.addEventListener('click', () => {
      for (const plot of plots) issue({ type: 'setBuildingEnabled', plot, enabled: false });
    });

    row.append(label, count, on, off);
    body.append(row);
    return {
      id,
      count,
      setPlots: (next: Array<{ x: number; y: number }>) => {
        plots = next;
      },
    };
  });

  return {
    root,
    update(state: GameState): void {
      const built = new Map<string, Array<{ x: number; y: number }>>();
      const running = new Map<string, number>();

      for (const gridRow of state.city.grid) {
        for (const plot of gridRow) {
          if (plot === null || plot.state !== 'complete') continue;
          const list = built.get(plot.buildingType) ?? [];
          list.push({ x: plot.coord.x, y: plot.coord.y });
          built.set(plot.buildingType, list);
          if (plot.enabled) running.set(plot.buildingType, (running.get(plot.buildingType) ?? 0) + 1);
        }
      }

      for (const row of rows) {
        const plots = built.get(row.id) ?? [];
        row.setPlots(plots);
        const live = running.get(row.id) ?? 0;
        setText(row.count, plots.length === 0 ? '—' : `${live}/${plots.length}`);
        row.count.className = plots.length > 0 && live === 0 ? 'row-value warn' : 'row-value';
      }
    },
  };
}
